<template>
  <div class="bulk-action__container">
    <div class="flex-between">
      <label class="bulk-action__panel flex-between">
        <input
          ref="selectAllCheck"
          type="checkbox"
          class="checkbox"
          :checked="allConversationsSelected"
          :indeterminate.prop="!allConversationsSelected"
          @change="selectAll($event)"
        />
        <span>
          {{
            $t('BULK_ACTION.CONVERSATIONS_SELECTED', {
              conversationCount: conversations.length,
            })
          }}
        </span>
      </label>
      <div class="bulk-action__actions flex-between">
        <woot-button
          v-tooltip="$t('BULK_ACTION.LABELS.ASSIGN_LABELS')"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="tag"
          class="margin-right-smaller"
          @click="showLabelActions = !showLabelActions"
        />
        <woot-button
          v-tooltip="$t('BULK_ACTION.UPDATE.CHANGE_STATUS')"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="repeat"
          class="margin-right-smaller"
          @click="showUpdateActions = !showUpdateActions"
        />
        <woot-button
          v-tooltip="$t('BULK_ACTION.ASSIGN_AGENT_TOOLTIP')"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="person-assign"
          class="margin-right-smaller"
          @click="showAgentsList = !showAgentsList"
        />
        <woot-button
          v-tooltip="$t('BULK_ACTION.ASSIGN_TEAM_TOOLTIP')"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="people-team-add"
          @click="showTeamsList = !showTeamsList"
        />
      </div>
      <transition name="popover-animation">
        <label-actions
          v-if="showLabelActions"
          class="label-actions-box"
          @assign="assignLabels"
          @close="showLabelActions = false"
        />
      </transition>
      <transition name="popover-animation">
        <update-actions
          v-if="showUpdateActions"
          class="update-actions-box"
          :selected-inboxes="selectedUniqueInboxes"
          :conversation-count="conversations.length"
          :show-resolve="!showResolvedAction"
          :show-reopen="!showOpenAction"
          :show-snooze="!showSnoozedAction"
          @update="updateConversations"
          @close="showUpdateActions = false"
        />
      </transition>
      <transition name="popover-animation">
        <agent-selector
          v-if="showAgentsList"
          class="agent-actions-box"
          :selected-inboxes="selectedUniqueInboxes"
          :conversation-count="conversations.length"
          @select="submit"
          @close="showAgentsList = false"
        />
      </transition>
      <transition name="popover-animation">
        <team-actions
          v-if="showTeamsList"
          class="team-actions-box"
          @assign-team="assignTeam"
          @close="showTeamsList = false"
        />
      </transition>
    </div>
    <div v-if="allConversationsSelected" class="bulk-action__alert">
      {{ $t('BULK_ACTION.ALL_CONVERSATIONS_SELECTED_ALERT') }}
    </div>
  </div>
</template>

<script>
import AgentSelector from './AgentSelector.vue';
import UpdateActions from './UpdateActions.vue';
import LabelActions from './LabelActions.vue';
import TeamActions from './TeamActions.vue';
import bulkActionsMixin from 'dashboard/mixins/bulkActionsMixin';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: {
    AgentSelector,
    UpdateActions,
    LabelActions,
    TeamActions,
  },
  mixins: [bulkActionsMixin, alertMixin],
  props: {
    conversations: {
      type: Array,
      default: () => [],
    },
    conversationList: {
      type: Array,
      default: () => [],
    },
    selectedInboxes: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showAgentsList: false,
      showUpdateActions: false,
      showLabelActions: false,
      showTeamsList: false,
      popoverPositions: {},
    };
  },
  computed: {
    allConversationsSelected() {
      return (
        this.conversationList.length === this.conversations.length &&
        this.conversationList.every(el => this.conversations.includes(el.id))
      );
    },
    selectedUniqueInboxes() {
      return [...new Set(this.selectedInboxes)];
    },
    showOpenAction() {
      return this.allSelectedConversationsStatus('open');
    },
    showResolvedAction() {
      return this.allSelectedConversationsStatus('resolved');
    },
    showSnoozedAction() {
      return this.allSelectedConversationsStatus('snoozed');
    },
  },
  methods: {
    allSelectedConversationsStatus(status) {
      if (!this.conversations.length) return false;
      return this.conversations.every(item => {
        return this.$store.getters.getConversationById(item).status === status;
      });
    },
    selectAll(e) {
      this.$emit('select-all-conversations', e.target.checked);
    },
    async submit(agent) {
      await this.onAssignAgentBulk(agent);
      this.$emit('clear');
    },
    async updateConversations(status) {
      await this.onUpdateConversationsBulk(status);
      this.$emit('clear');
    },
    async assignLabels(labels) {
      await this.onAssignLabelsBulk(labels);
      this.$emit('clear');
    },
    async assignTeam(team) {
      await this.onAssignTeamsForBulk(team);
      this.$emit('clear');
    },
  },
};
</script>

<style scoped lang="scss">
// For RTL direction view
.app-rtl--wrapper {
  .bulk-action__actions {
    ::v-deep .button--only-icon:last-child {
      margin-right: var(--space-smaller);
    }
  }
}

.bulk-action__container {
  border-bottom: 1px solid var(--s-100);
  padding: var(--space-normal);
  position: relative;
}

.bulk-action__panel {
  cursor: pointer;

  span {
    font-size: var(--font-size-mini);
    margin: 0 var(--space-smaller);
  }

  input[type='checkbox'] {
    cursor: pointer;
    margin: var(--space-zero);
  }
}

.bulk-action__alert {
  background-color: var(--y-50);
  border-radius: var(--border-radius-small);
  border: 1px solid var(--y-300);
  color: var(--y-700);
  font-size: var(--font-size-mini);
  margin-top: var(--space-small);
  padding: var(--space-smaller) var(--space-small);
}

.popover-animation-enter-active,
.popover-animation-leave-active {
  transition: transform ease-out 0.1s;
}

.popover-animation-enter {
  opacity: 0;
  transform: scale(0.95);
}

.popover-animation-enter-to {
  opacity: 1;
  transform: scale(1);
}

.popover-animation-leave {
  opacity: 1;
  transform: scale(1);
}

.popover-animation-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.label-actions-box {
  --triangle-position: 8.5rem;
}
.update-actions-box {
  --triangle-position: 5.6rem;
}
.agent-actions-box {
  --triangle-position: 2.8rem;
}
.team-actions-box {
  --triangle-position: 0.2rem;
}
</style>
